import { useMemo } from 'react';
import type { FC } from 'react';

import { useCms } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Heading, InputSelect, TextArea, FilePicker, NotificationBox } from '@eneco/sparky';
import type { FilePickerProps } from '@eneco/sparky/types';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';

import { hintOf } from './hint';
import { stripOuterParagraph } from '../DynamicPriceCard';
import { optionsOf } from './options';
import type { FormFields } from './types';
import type { ContactFormAction, ContactFormFields } from '../contact-form-content.types';

type Props = {
  register: UseFormRegister<FormFields>;
  formFields: ContactFormFields;
  inquiryTitle: string | undefined;
  hideInquiryTypeFormFieldCheckbox: boolean | undefined;
  actions: ContactFormAction[];
  errors: FieldErrors<FormFields>;
  selectedTopic: string;
} & Pick<FilePickerProps, 'files' | 'setFiles' | 'hasError' | 'setHasError'>;

// The topic an action is for, and its message: Sanity's flat `fieldValue` and `messageContent` rich-text
// entry, or Sitecore's `data.fieldValue` and `content.messageContent`.
const actionValue = (action: ContactFormAction) => action.fieldValue ?? action.data?.fieldValue;
const actionMessage = (action: ContactFormAction) =>
  action.richText?.find(entry => entry.role === 'messageContent')?.content ?? action.content?.messageContent;

const InquiryInputs: FC<Props> = ({
  register,
  formFields,
  inquiryTitle,
  hideInquiryTypeFormFieldCheckbox,
  actions,
  errors,
  selectedTopic,
  files,
  setFiles,
  hasError,
  setHasError,
}) => {
  const { adapter } = useCms();
  const { inquiryContentFormField, inquiryTopicFormField, inquiryTypeFormField } = formFields;
  const inquiryTypeOptions = optionsOf(inquiryTopicFormField);
  const topicNotification = useMemo(() => {
    const formAction = actions.find(action => actionValue(action) === selectedTopic);
    if (formAction) {
      const title = inquiryTypeOptions.find(option => option.value === selectedTopic)?.label ?? '';
      // Inline on Sitecore; Sanity's Portable Text arrives as one paragraph.
      return { title, text: stripOuterParagraph(adapter.toRichText(actionMessage(formAction))) };
    }
    return null;
  }, [selectedTopic, actions, adapter, inquiryTypeOptions]);

  return (
    <>
      <Heading size="S" as="h3">
        {inquiryTitle}
      </Heading>
      {!hideInquiryTypeFormFieldCheckbox && (
        <InputSelect
          placeholder={inquiryTypeFormField.placeholder ?? ''}
          hint={hintOf(inquiryTypeFormField.hint)}
          label={inquiryTypeFormField.label ?? ''}
          options={optionsOf(inquiryTypeFormField)}
          {...register('inquiryType')}
          error={errors.inquiryType?.message}
        />
      )}
      <InputSelect
        placeholder={inquiryTopicFormField.placeholder ?? ''}
        label={inquiryTopicFormField.label ?? ''}
        options={inquiryTypeOptions}
        hint={hintOf(inquiryTopicFormField.hint)}
        {...register('inquiryTopic')}
        error={errors.inquiryTopic?.message}
      />
      {topicNotification ? (
        <NotificationBox isAlert text={<RichText html={topicNotification.text} />} title={topicNotification.title} />
      ) : null}
      <TextArea
        label={inquiryContentFormField.label ?? ''}
        hint={hintOf(inquiryContentFormField.hint)}
        placeholder={inquiryContentFormField.placeholder ?? ''}
        rows={6}
        error={errors.inquiryContent?.message}
        maxLength={1000}
        {...register('inquiryContent')}
      />
      <FilePicker files={files} setFiles={setFiles} setHasError={setHasError} hasError={hasError} name="files" />
    </>
  );
};

export default InquiryInputs;
